import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
    Radio,
    Skeleton,
    TableCell,
    TableRow,
    Theme,
    Typography
} from "@mui/material";
import { MandateListItem, MandatesListResBody } from "../../../redux-store/types/investorProfile";
import {
    FormikValues,
    useField,
    useFormikContext
} from "formik";
import { DialogCloseIcon } from "../../../assets/SvgIcons";
import { useState } from "react";
import CustomTable from "../common/customTable";
import { TableHeaderData } from "../types";
import { toINR } from "../../../utils/utilityFunctions";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { RootStateType } from "../../../redux-store/reducers";

export const MandateInfoBox = ({
    name,
    isLoading,
    isDisabled,
    activeMandateList,
    id,
}: {
    name: string;
    isLoading: boolean;
    isDisabled: boolean;
    activeMandateList: Array<MandateListItem>;
    id: string | number;
}): JSX.Element => {
    const { values } = useFormikContext() as { values: FormikValues }
    const [_, meta] = useField(name)
    const errorText = (meta.error && meta.touched) ? meta.error : '';

    const selectedMandateObj = (activeMandateList ?? [])?.find(obj => `${obj?.mandateId ?? ""}` === `${values?.fund?.[id]?.sip?.mandate ?? ""}`)

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
            }}
        >
            {isLoading && (
                <>
                    <Skeleton animation="wave" width={100} />
                    <Skeleton
                        variant="rectangular"
                        height={50}
                        sx={{ borderRadius: '10px' }}
                    />
                </>
            )}
            {!isLoading && (
                <>
                    <Typography
                        variant="body_500"
                        sx={(theme: Theme) => ({
                            textWrap: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: theme.palette.text.secondary
                        })}
                    >
                        Mandate
                    </Typography>
                    <Box
                        sx={(theme: Theme) => ({
                            display: "flex",
                            alignItems: "center",
                            background: errorText ? theme.palette.error[50] : theme.palette.primary[50],
                            padding: '15px',
                            borderRadius: '10px',
                            opacity: isDisabled ? 0.5 : 1,
                        })}
                    >
                        <Typography
                            variant="body_500"
                            sx={(theme: Theme) => ({
                                color: theme.palette.primary[500],
                                opacity: isDisabled ? 0.9 : 1,
                                fontSize: { lg: '16px', md: '16px', sm: '14px', xs: '14px' }
                            })}
                        >
                            {selectedMandateObj?.displayText ?? ""}
                        </Typography>
                    </Box>
                </>
            )}
            {errorText &&
                (isLoading ? (
                    <Skeleton variant="text" animation="wave" width={200} />
                ) : <Typography variant="body_400" sx={(theme: Theme) => ({
                    color: theme.palette.error[500]
                })}>
                    {errorText || ''}
                </Typography>)
            }

        </Box >
    )
}

const tableHeaderData: TableHeaderData[] = [
    {
        id: "checkbox",
        label: "",
        align: "left",
        isSortable: false
    },
    {
        id: "bankName",
        label: "BANK NAME",
        align: "center",
        isSortable: false
    },
    {
        id: "accountNumber",
        label: "ACCOUNT NUMBER",
        align: "center",
        isSortable: false
    },
    {
        id: "amount",
        label: "AMOUNT",
        align: "center",
        isSortable: false
    },
    {
        id: "mandateId",
        label: "MANDATE ID",
        align: "center",
        isSortable: false
    },
    {
        id: "startDate",
        label: "START DATE",
        align: "center",
        isSortable: false
    },
    {
        id: "endDate",
        label: "END DATE",
        align: "center",
        isSortable: false
    },
    {
        id: "status",
        label: "STATUS",
        align: "center",
        isSortable: false
    },
]

export const MandateTableBody = ({
    data,
    selectedMandateId,
    handleRadioBtnClick
}: {
    data: Array<MandateListItem>;
    selectedMandateId: string;
    handleRadioBtnClick: (mandateId: string) => void
}) => {

    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);

    return (
        <>
            {data.map((obj: MandateListItem) => (
                <TableRow
                    key={obj.mandateId}
                    sx={(theme: Theme) => ({
                        '& td': {
                            paddingTop: "20px",
                            paddingBottom: "15px",
                            borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`,
                            ':first-of-type': {
                                paddingLeft: "0px",
                            }
                        },
                    })}
                >
                    <TableCell align="left" width="50px">
                        <Radio
                            checked={obj?.mandateId === selectedMandateId}
                            onChange={() => handleRadioBtnClick(obj?.mandateId ?? "")}
                            value=""
                            name="mandate-select"
                        />
                    </TableCell>
                    <TableCell align="center" width="200px">
                        <Typography
                            variant="body_500"
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.primary
                            })}
                        >
                            {obj?.bankName ?? ""}
                        </Typography>
                    </TableCell>
                    <TableCell align="center" width="200px">
                        <Typography
                            variant="body_500"
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.primary
                            })}
                        >
                            {obj?.accountNo ?? ""}
                        </Typography>
                    </TableCell>
                    <TableCell align="center" width="200px">
                        <Typography
                            variant="body_500"
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.primary
                            })}
                        >
                            {toINR({
                                value: obj?.amount ?? "",
                                locale,
                                currencyCode
                            })}
                        </Typography>
                    </TableCell>
                    <TableCell align="center" width="200px">
                        <Typography
                            variant="body_500"
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.primary
                            })}
                        >
                            {obj?.mandateId ?? ""}
                        </Typography>
                    </TableCell>
                    <TableCell align="center" width="200px">
                        <Typography
                            variant="body_500"
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.primary
                            })}
                        >
                            {obj?.startDate ? dayjs(obj.startDate).format("DD/MM/YYYY") : "-"}
                        </Typography>
                    </TableCell>
                    <TableCell align="center" width="200px">
                        <Typography
                            variant="body_500"
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.primary
                            })}
                        >
                            {obj?.endDate ? dayjs(obj.endDate).format("DD/MM/YYYY") : "-"}
                        </Typography>
                    </TableCell>
                     <TableCell align="center">
                        <Typography
                            variant="body_500"
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.primary
                            })}
                        >
                            {obj?.status ?? "-"}
                        </Typography>
                    </TableCell>
                </TableRow>
            ))
            }
        </>
    )
}

export const MandateSelectionDialog = ({
    open,
    onClose,
    currentMandateId,
    mandateList,
    handleMandateSelection
}: {
    open: boolean;
    onClose: () => void;
    currentMandateId: string;
    mandateList: MandateListItem[];
    handleMandateSelection: (mandateId: string) => void
}): JSX.Element => {
    const [selectedMandateId, setSelectedMandateId] = useState<string>(currentMandateId)

    const handleSelectMandateRadioBtn = (mandateId: string) => {
        setSelectedMandateId(mandateId)
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth='lg'
            sx={(theme: Theme) => ({
                '& .MuiPaper-root': {
                    borderRadius: '25px',
                    backgroundColor: theme.palette.common.white,
                    padding: "10px",
                    width: "1063px",
                    maxHeight: "80%"
                },
            })}
        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    padding: "10px"
                }}
            >
                <Box>
                    <Typography
                        variant="h2"
                        sx={(theme: Theme) => ({
                            lineHeight: "25px",
                            letterSpacing: "0.01px",
                            color: theme.palette.text.primary,
                        })}
                    >
                        Mandate Selection
                    </Typography>
                </Box>
                <IconButton
                    aria-label="dialog-close"
                    onClick={onClose}
                    sx={{
                        padding: "5px",
                        "&:disabled": {
                            opacity: 0.5
                        }
                    }}
                >
                    <DialogCloseIcon />
                </IconButton>
            </Box>
            <DialogContent
                sx={{
                    maxHeight: "100%",
                    overflowY: "auto",
                    padding: "10px 20px 10px 10px"
                }}
            >
                <CustomTable
                    tableBody={
                        <MandateTableBody
                            data={mandateList}
                            selectedMandateId={selectedMandateId}
                            handleRadioBtnClick={handleSelectMandateRadioBtn}
                        />
                    }
                    tableData={mandateList}
                    tableHeaderData={tableHeaderData}
                    tableDataCount={(mandateList ?? []).length}
                    tableBodyLoaderRowCustomStyles={{
                        '& td:first-of-type': {
                            paddingLeft: "0px"
                        },
                    }}
                    tableHeadRowCustomStyles={{
                        '& th:first-of-type': {
                            paddingLeft: "0px"
                        }
                    }}
                    tableBodyCustomStyles={{
                        '& tr:last-child td': {
                            border: 0
                        },
                    }}
                    isPaginationVisible={false}
                />
            </DialogContent>
            <DialogActions
                sx={{ justifyContent: "center" }}
            >
                <Button
                    variant="contained"
                    sx={(theme: Theme) => ({
                        ...theme.typography.body_600,
                        padding: "10px",
                        width: "50%"
                    })}
                    onClick={() => handleMandateSelection(selectedMandateId)}
                >
                    Okay
                </Button>
            </DialogActions>
        </Dialog >
    )
}